/**
 * useInstitutions — TanStack Query wrapper around
 * ``GET /api/v1/connectors/open_banking/institutions?country={cc}``.
 *
 * The BE adapter (Tink / Fabrick / Enable Banking) is opaque to the FE
 * per the white-label memory rule — the response carries pure
 * ``InstitutionDTO`` rows with no aggregator name leak.
 *
 * Cache strategy: ``staleTime = 1 h``. The institution list rarely
 * changes (banks join/leave the PSD2 register on a monthly cadence at
 * most), and the openBankingSpec wizard remounts it every time the
 * operator re-opens the bank picker.
 *
 * Empty-list fallback: when the BE returns ``{ institutions: [] }``
 * (Tink Link path requires a user-scoped token before listing) the
 * caller falls back to the provider's hosted picker, opened in the
 * subsequent "consent" step.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getOpenBankingInstitutions, type InstitutionDTO } from "@ando/core";

const INSTITUTIONS_STALE_TIME_MS = 60 * 60_000;
const INSTITUTIONS_DEFAULT_LIMIT = 50;

export function institutionsQueryKey(country: string): readonly unknown[] {
  return ["connectors", "open_banking", "institutions", country.toUpperCase()] as const;
}

export function useInstitutions(
  country: string,
  limit: number = INSTITUTIONS_DEFAULT_LIMIT,
): UseQueryResult<readonly InstitutionDTO[]> {
  return useQuery<readonly InstitutionDTO[]>({
    queryKey: institutionsQueryKey(country),
    queryFn: () => getOpenBankingInstitutions(country, limit),
    enabled: country.length === 2,
    staleTime: INSTITUTIONS_STALE_TIME_MS,
    retry: 1,
  });
}
